import { getSearchResults_seoSegmentationSearchResults_SEOSegmentationSearchResultsSuccess as SEOSegmentationSearchResultsSuccessType } from '@/__generated__/getSearchResults';
import {
  seoSegmentationListingPage_seoSegmentationDetails_SEOSegmentationDetailsSuccess_segmentationDetails_crossSell as CrossSell,
  seoSegmentationListingPage_seoSegmentationStats_SEOSegmentationStatsSuccess as SEOSegmentationStats,
} from '@/__generated__/seoSegmentationListingPage';
import { SEOSegmentationPageType, UsStateCode } from '@/__generated__/globalTypes';

import { SEARCH_RESULT_TYPE, ServiceIdForMember } from '@/constants';
import SearchResults from './SearchResults';
import JobSearchResults from './JobSearchResults';
import ArchivedJobSearchResults from './ArchivedJobSearchResults';
import { Loading } from './Loading';
import NoResults from './NoResults';
import HybridSearchResults from './HybridSearchResults';
import ItemListSchema from './ItemListSchema';
import LocalBusinessSchema from './LocalBusinessSchema';
import ProductSchema from './ProductSchema';

interface SearchResultsControllerProps {
  caregiverSearchResults: SEOSegmentationSearchResultsSuccessType['caregiverSearchResults'];
  city: string | null;
  conversionHeader: string;
  crossSell: CrossSell | null;
  enrollmentURL: string;
  isClientSearchResultsQuery: boolean;
  isLoading: boolean;
  jobSearchResults: SEOSegmentationSearchResultsSuccessType['jobSearchResults'];
  archivedJobSearchResults: SEOSegmentationSearchResultsSuccessType['archivedJobSearchResults'];
  pageType: SEOSegmentationPageType;
  providerSearchResults: SEOSegmentationSearchResultsSuccessType['providerSearchResults'];
  serviceId: string;
  serviceIdForMember: ServiceIdForMember;
  startIndex: number;
  state: UsStateCode | null;
  totalNumberOfResults: number;
  segmentationStats: SEOSegmentationStats['caregiverStats'];
  smbHeadline: string;
}

const SearchResultsController = ({
  caregiverSearchResults,
  city,
  conversionHeader,
  enrollmentURL,
  isClientSearchResultsQuery,
  isLoading,
  jobSearchResults,
  archivedJobSearchResults,
  pageType,
  providerSearchResults,
  serviceId,
  serviceIdForMember,
  startIndex,
  totalNumberOfResults,
  segmentationStats,
  smbHeadline,
}: SearchResultsControllerProps) => {
  const showProductSchema = (segmentationStats?.totalReviewCount ?? 0) >= 5;
  // eslint-disable-next-line complexity
  const renderResults = () => {
    // Loading
    if (isLoading) return <Loading />;

    // No Results
    if (
      caregiverSearchResults.length === 0 &&
      providerSearchResults.length === 0 &&
      jobSearchResults.length === 0 &&
      archivedJobSearchResults.length === 0
    ) {
      return <NoResults />;
    }

    // Caregiver Results
    if (caregiverSearchResults.length > 0 && providerSearchResults.length === 0) {
      return (
        <>
          <SearchResults
            results={caregiverSearchResults}
            resultType={SEARCH_RESULT_TYPE.CAREGIVER}
            isClientSearchResultsQuery={isClientSearchResultsQuery}
            serviceId={serviceId}
            serviceIdForMember={serviceIdForMember}
          />

          <ItemListSchema
            results={caregiverSearchResults}
            totalNumberOfResults={totalNumberOfResults}
            startIndex={startIndex}
          />

          {segmentationStats && showProductSchema && (
            <ProductSchema
              avgReviewRating={segmentationStats.avgReviewRating}
              totalReviewCount={segmentationStats.totalReviewCount}
            />
          )}
        </>
      );
    }

    // Provider Results
    if (providerSearchResults.length > 0 && caregiverSearchResults.length === 0) {
      return (
        <>
          <SearchResults
            results={providerSearchResults}
            resultType={SEARCH_RESULT_TYPE.PROVIDER}
            isClientSearchResultsQuery={isClientSearchResultsQuery}
            serviceId={serviceId}
            serviceIdForMember={serviceIdForMember}
          />

          <LocalBusinessSchema results={providerSearchResults} />

          {segmentationStats && showProductSchema && (
            <ProductSchema
              avgReviewRating={segmentationStats.avgReviewRating}
              totalReviewCount={segmentationStats.totalReviewCount}
            />
          )}
        </>
      );
    }

    // Job and Archived Job Results
    if (
      (jobSearchResults.length > 0 && archivedJobSearchResults.length === 0) ||
      (jobSearchResults.length === 0 && archivedJobSearchResults.length > 0) ||
      (jobSearchResults.length > 0 && archivedJobSearchResults.length > 0)
    ) {
      return (
        <>
          {jobSearchResults.length > 0 && (
            <>
              <JobSearchResults
                results={jobSearchResults}
                conversionHeader={conversionHeader}
                enrollmentURL={enrollmentURL}
                resultType={SEARCH_RESULT_TYPE.JOB}
                isClientSearchResultsQuery={isClientSearchResultsQuery}
                showConversionSectionFrequency={5}
                pageType={pageType}
              />

              <ItemListSchema
                results={jobSearchResults}
                totalNumberOfResults={totalNumberOfResults}
                startIndex={startIndex}
              />
            </>
          )}
          {archivedJobSearchResults.length > 0 && (
            <ArchivedJobSearchResults
              city={city}
              conversionHeader={conversionHeader}
              enrollmentURL={enrollmentURL}
              pageType={pageType}
              results={archivedJobSearchResults}
              resultType={SEARCH_RESULT_TYPE.JOB}
            />
          )}
        </>
      );
    }

    // Hybrid Results (i.e. /child-care)
    return (
      <>
        <HybridSearchResults
          caregiverSearchResults={caregiverSearchResults}
          isClientSearchResultsQuery={isClientSearchResultsQuery}
          providerSearchResults={providerSearchResults}
          serviceId={serviceId}
          serviceIdForMember={serviceIdForMember}
          smbHeadline={smbHeadline}
        />

        <ItemListSchema
          results={caregiverSearchResults}
          totalNumberOfResults={totalNumberOfResults}
          startIndex={startIndex}
        />

        <LocalBusinessSchema results={providerSearchResults} />

        {segmentationStats && showProductSchema && (
          <ProductSchema
            avgReviewRating={segmentationStats.avgReviewRating}
            totalReviewCount={segmentationStats.totalReviewCount}
          />
        )}
      </>
    );
  };

  return renderResults();
};
export default SearchResultsController;
